import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useUserRegistrationContext } from '@/context/UserRegistrationContext';

export const useAccountFinder = () => {
  const queryClient = useQueryClient();
  // Track component state
  const [isResetting, setIsResetting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isTokenSet, setIsTokenSet] = useState(false);

  const {
    setSelectedAccountUserName,
    setScopedToken,
    scopedToken,
    setHasDateOfBirth,
    setHasMobileNumber,
    setBlackholeAccountUsers,
    setSelectedUserId,
    selectedAccountUserName,
    selectedUserId,
    hasDateOfBirth,
    hasMobileNumber,
    blackholeAccountUsers,
  } = useUserRegistrationContext();

  // Track when the scopedToken is set/cleared to ensure proper mutation chain completion
  useEffect(() => {
    setIsTokenSet(!!scopedToken);
  }, [scopedToken]);

  // Initial state setup effect - runs once when component mounts
  // This ensures we have the correct state whether starting new or returning to the form
  useEffect(() => {
    const setupState = async () => {
      if (hasInitialized) return;

      setIsResetting(true);

      try {
        // Check if we're starting a new journey (no existing data)
        const isNewJourney = !scopedToken && !selectedAccountUserName.givenName;

        if (isNewJourney) {
          // For new journeys, reset everything to ensure a clean start
          setScopedToken('');
          setSelectedAccountUserName({ givenName: '', familyName: '' });
          setSelectedUserId('');
          setHasDateOfBirth(false);
          setHasMobileNumber(false);
          setBlackholeAccountUsers([]);

          // Invalidate queries for fresh data
          await queryClient.invalidateQueries({
            queryKey: ['accountIdentification'],
          });
          await queryClient.invalidateQueries({
            queryKey: ['userRegistrationStatus'],
          });
          await queryClient.resetQueries({
            queryKey: ['verifyIdentityFallback'],
          });
        }
        // If not a new journey, we keep all existing state (including scopedToken)
        // This ensures the address dropdown and form remain populated when navigating back

        setHasInitialized(true);
      } catch (error) {
        console.error('Error setting up state:', error);
      } finally {
        setIsResetting(false);
      }
    };

    setupState();
  }, [hasInitialized]);

  return {
    isResetting,
    setIsResetting,
    hasInitialized,
    setHasInitialized,
    isTokenSet,
    setIsTokenSet,
    scopedToken,
    setScopedToken,
    selectedAccountUserName,
    setSelectedAccountUserName,
    selectedUserId,
    setSelectedUserId,
    hasDateOfBirth,
    setHasDateOfBirth,
    hasMobileNumber,
    setHasMobileNumber,
    blackholeAccountUsers,
    setBlackholeAccountUsers,
  };
};
